import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import type { Session } from '@supabase/supabase-js';
import { AppShell } from '@/components/layout/AppShell';
import { LoginPage } from '@/pages/LoginPage';
import { SignupPage } from '@/pages/SignupPage';
import { DiagnosticPage } from '@/pages/DiagnosticPage';
import { DiagnosticReturnPage } from '@/pages/DiagnosticReturnPage';
import { DiagnosticLabPage } from '@/pages/DiagnosticLabPage';
import { OptOutPage } from '@/pages/OptOutPage';
import { WelcomePage } from '@/pages/WelcomePage';
import { OAuthCompletePage } from '@/pages/OAuthCompletePage';
import { ToolsPage } from '@/pages/ToolsPage';
import { AppRoute, ToolRoute } from '@/pages/AppRoutes';
import { FoldersPage } from '@/pages/FoldersPage';
import { FolderPage } from '@/pages/FolderPage';
import { TemplatesPage } from '@/pages/TemplatesPage';
import { FlowComposerPage } from '@/pages/FlowComposerPage';
import { ExecutionDetailPage } from '@/pages/ExecutionDetailPage';
import { ConnectionsPage } from '@/pages/ConnectionsPage';
import { ContextPage } from '@/pages/ContextPage';
import { ActivityPage } from '@/pages/ActivityPage';
import { ThreadPage } from '@/pages/ThreadPage';
import { AccountsLayout, AccountsIndex } from '@/pages/AccountsLayout';
import { SettingsPage } from '@/pages/SettingsPage';
import { UsagePage } from '@/pages/UsagePage';
import { AppLabPage } from '@/pages/AppLabPage';

const LegacyProfileRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={`/profil${search}`} replace />;
};

interface AppRouterProps {
  session: Session | null;
}

const LegacyReadToolRedirect = () => {
  const { toolAppKey } = useParams();
  return <Navigate to={toolAppKey ? `/forge?toolAppKey=${encodeURIComponent(toolAppKey)}` : '/forge'} replace />;
};

const LegacyAppSettingsRedirect = () => {
  const { toolAppKey } = useParams();
  return <Navigate to={`/apps/${encodeURIComponent(toolAppKey ?? '')}`} replace />;
};

export const AppRouter = ({ session }: AppRouterProps) => {
  if (!session) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/diagnostic" element={<DiagnosticPage />} />
        <Route path="/diagnostic/retour" element={<DiagnosticReturnPage />} />
        <Route path="/opposition/:token" element={<OptOutPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="/signup" element={<Navigate to="/bienvenue" replace />} />
      <Route path="/bienvenue" element={<WelcomePage />} />
      {/* Online diagnostic: a public lead magnet, the same with or without a Domino session. */}
      <Route path="/diagnostic" element={<DiagnosticPage />} />
      <Route path="/diagnostic/retour" element={<DiagnosticReturnPage />} />
      {/* Diagnostic sandbox: Domino operators only (checked by the function), no identity, nothing saved. */}
      <Route path="/lab/diagnostic" element={<DiagnosticLabPage />} />
      {/* Refusal link of a prospecting e-mail: public, the same with or without a session. */}
      <Route path="/opposition/:token" element={<OptOutPage />} />
      {/* Connection window: no app shell (audit S4). */}
      <Route path="/oauth/complete" element={<OAuthCompletePage />} />
      <Route element={<AppShell key={session.user.id} userId={session.user.id} />}>
        <Route path="/" element={<Navigate to="/tasks" replace />} />
        <Route path="/home" element={<Navigate to="/tasks" replace />} />
        <Route path="/tasks" element={<ActivityPage />} />
        {/* Objectives live in Profil since 08/10/2026 (step ② of the qualification). */}
        <Route path="/objectives" element={<Navigate to="/profil?tab=objectives" replace />} />
        <Route element={<AccountsLayout />}>
          <Route path="/accounts" element={<AccountsIndex />} />
          <Route path="/threads/:subjectId" element={<ThreadPage />} />
        </Route>
        <Route path="/activity" element={<Navigate to="/tasks" replace />} />
        <Route path="/tools" element={<ToolsPage />} />
        <Route path="/tools/:toolKey" element={<ToolRoute />} />
        <Route path="/forge" element={<FlowComposerPage />} />
        {/* Layout lab: fictional app scenarios, development only. */}
        {import.meta.env.DEV && <Route path="/lab/apps" element={<AppLabPage />} />}
        <Route path="/apps/:toolAppKey" element={<AppRoute />} />
        <Route path="/apps/:toolAppKey/o/:recordId" element={<AppRoute />} />
        <Route path="/folders" element={<FoldersPage />} />
        <Route path="/folders/:folderId" element={<FolderPage />} />
        <Route path="/forge/data-lens" element={<LegacyReadToolRedirect />} />
        {/* An app is managed from its « … » since 21/10/2026. */}
        <Route path="/apps/:toolAppKey/settings" element={<LegacyAppSettingsRedirect />} />
        <Route
          path="/forge/data-lens/:toolAppKey/edit"
          element={<LegacyReadToolRedirect />}
        />
        <Route path="/templates" element={<TemplatesPage />} />
        <Route path="/executions/:id" element={<ExecutionDetailPage />} />
        <Route path="/profil" element={<ContextPage />} />
        {/* Profil was served at /context until 13/10/2026. */}
        <Route path="/context" element={<LegacyProfileRedirect />} />
        <Route path="/connections" element={<ConnectionsPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/usage" element={<UsagePage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};
